import React from 'react';
import { Card, CardBody, CardHeader, Divider, Chip, Button } from '@nextui-org/react';
import { NoteWithCourseInfo } from '../../services/notesService';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AnimatedHeartIcon from '../AnimatedHeartIcon';
import WatchVideoButton from '../WatchVideoButton';

interface NoteCardProps {
    note: NoteWithCourseInfo;
    onUpdate?: () => void;
}

const NoteCard: React.FC<NoteCardProps> = ({ note, onUpdate }) => {
    const navigate = useNavigate();

    // Convert Firestore Timestamp to Date
    const lastUpdated = note.notes.updatedAt?.toDate ? note.notes.updatedAt.toDate() : new Date();
    const completedAt = note.completedAt?.toDate ? note.completedAt.toDate() : null;

    // Extract collection path from parentDocPath for YoutubeSection
    const pathParts = note.parentDocPath.split('/');
    const docId = pathParts[pathParts.length - 1];
    pathParts.pop(); // Remove docId
    const collectionPath = pathParts.join('/');

    // Construct doc object for YoutubeSection
    const doc = {
        id: docId,
        youtubeLink: note.youtubeLink,
        description: note.courseDescription || note.courseName,
        name: note.courseName,
        // Add any other fields that YoutubeSection might expect
    };

    const handleNavigate = () => {
        // Navigate to the course page
        const coursePath = note.parentDocPath.replace(/\/[^/]+$/, ''); // Remove the last segment (docId)
        navigate(`/${coursePath}`);
    };

    const createHtmlPreview = (htmlContent: string, maxLength: number) => {
        if (!htmlContent) return '';

        // Remove HTML tags for length calculation
        const textContent = htmlContent.replace(/<[^>]*>/g, '');

        if (textContent.length <= maxLength) {
            return htmlContent;
        }

        // Truncate and add ellipsis
        const truncatedText = textContent.substring(0, maxLength) + '...';

        // Simple approach: just return the truncated text wrapped in a span
        // In a real implementation, you might want to preserve some HTML structure
        return `<span>${truncatedText}</span>`;
    };

    return (
        <>
            <Card className='w-full h-full hover:shadow-lg transition-shadow flex flex-col'>
                <CardHeader className='flex justify-between items-start gap-4 flex-shrink-0 h-24'>
                    <div className='flex flex-col gap-1 flex-1 h-full overflow-hidden'>
                        <h3 className='text-lg font-semibold line-clamp-1'>{note.courseName}</h3>
                        <p className='text-sm text-default-500 line-clamp-2'>{note.courseDescription}</p>
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                        {note.isFavorited && (
                            <AnimatedHeartIcon
                                isFavorited={note.isFavorited}
                                size={40}
                                speed={0.4}
                                loop={true}
                                className='flex items-center justify-center'
                                title='Favorited'
                                animationType='badge'
                            />
                        )}
                        <Chip
                            color={note.completionStatus ? "success" : "warning"}
                            variant='flat'
                            size='sm'
                        >
                            {note.completionStatus ? "Completed" : "In Progress"}
                        </Chip>
                    </div>
                </CardHeader>
                <Divider />
                <CardBody className='flex flex-col flex-1'>
                    {/* Note Preview with fixed height */}
                    <div className='bg-default-100 rounded-lg p-3 h-20 overflow-hidden mb-3'>
                        <div
                            className='text-sm line-clamp-3 select-none pointer-events-none prose prose-sm max-w-none 
                                       prose-p:my-0 prose-headings:mt-0 prose-headings:mb-1
                                       prose-ul:my-0 prose-ol:my-0 prose-li:my-0
                                       prose-strong:font-semibold prose-em:italic
                                       dark:prose-invert'
                            dangerouslySetInnerHTML={{ __html: createHtmlPreview(note.notes.content, 180) }}
                        />
                    </div>

                    {/* Metadata */}
                    <div className='flex flex-wrap gap-2 text-sm text-default-500 mb-2'>
                        <span>Last updated: {format(lastUpdated, 'MMM d, yyyy')}</span>
                        {completedAt && (
                            <>
                                <span>•</span>
                                <span>Completed: {format(completedAt, 'MMM d, yyyy')}</span>
                            </>
                        )}
                    </div>

                    {/* Spacer to push buttons to bottom */}
                    <div className='flex-1'></div>

                    {/* Action Buttons - Always at bottom */}
                    <div className='flex gap-2 mt-auto'>
                        <Button
                            color='primary'
                            onPress={handleNavigate}
                        >
                            Go to Course
                        </Button>
                        {note.youtubeLink && (
                            <WatchVideoButton
                                doc={doc}
                                collectionPath={collectionPath}
                                initialProgress={{
                                    completionStatus: note.completionStatus,
                                    isFavorited: note.isFavorited || false,
                                    notes: note.notes
                                }}
                                onProgressUpdate={onUpdate}
                            />
                        )}
                    </div>
                </CardBody>
            </Card>
        </>
    );
};

export default NoteCard; 